/**
 * AppSignal Error Monitoring Configuration
 *
 * This module configures and exports the AppSignal client instance for frontend
 * error tracking and performance monitoring.
 *
 * Configuration:
 * - Configured via REACT_APP_APPSIGNAL_KEY environment variable
 * - The key is injected at build time by Create React App
 * - The frontend API key is safe to expose in client-side code
 *
 * Behavior:
 * - If REACT_APP_APPSIGNAL_KEY is present and non-empty, AppSignal is initialized
 * - If the key is missing or empty, a mock instance is provided to prevent runtime errors
 * - Mock instance has noop methods for all AppSignal API calls
 *
 * Disabling AppSignal:
 * - To disable AppSignal in any environment, remove or empty the REACT_APP_APPSIGNAL_KEY
 *   variable in the corresponding .env file (.env, .env.dev, .env.test, etc.)
 *
 * Usage:
 * - Import from lib abstraction layer: `import { appsignal } from "lib"`
 * - Use with ErrorBoundary: `<ErrorBoundary instance={appsignal}>`
 * - Manual error reporting: `appsignal.sendError(error)`
 */

import Appsignal from "@appsignal/javascript";

const APPSIGNAL_KEY = process.env.REACT_APP_APPSIGNAL_KEY;

/**
 * Mock AppSignal instance that provides noop implementations
 * Used when AppSignal is not configured to prevent runtime errors
 */
const createMockInstance = () => {
  const noop = () => {};

  // Log warning in development mode
  if (process.env.NODE_ENV === "development") {
    console.warn(
      "AppSignal: No API key configured. AppSignal is disabled. " +
        "Set REACT_APP_APPSIGNAL_KEY environment variable to enable error monitoring."
    );
  }

  return {
    sendError: noop,
    addBreadcrumb: noop,
    setAction: noop,
    setTags: noop,
    setParams: noop,
    demo: noop,
    wrap: fn => fn
  };
};

/**
 * Build the AppSignal configuration object
 * Exported for testability - allows tests to verify config without mocking the constructor
 *
 * @param {string} apiKey - The AppSignal API key
 * @returns {Object} The configuration object to pass to AppSignal
 */
export const getAppSignalConfig = (apiKey = APPSIGNAL_KEY) => {
  const config = {
    key: apiKey,
    namespace: "frontend"
  };

  // Add git revision for deploy markers if available
  const gitRevision = process.env.REACT_APP_GIT_REVISION;
  if (gitRevision) {
    config.revision = gitRevision;
  }

  return config;
};

/**
 * Initialize AppSignal if key is present, otherwise return mock instance
 */
const initializeAppSignal = () => {
  // If no API key is configured, don't initialize AppSignal at all
  if (!APPSIGNAL_KEY || APPSIGNAL_KEY.trim() === "") {
    return createMockInstance();
  }

  try {
    const config = getAppSignalConfig();

    // Add git revision for deploy markers if available
    if (config.revision) {
      console.info(
        "AppSignal: Using git revision:",
        config.revision.substring(0, 8) + "..."
      );
    }

    console.info(
      "AppSignal: Initializing with key:",
      APPSIGNAL_KEY.substring(0, 8) + "..."
    );
    const instance = new Appsignal(config);
    console.info("AppSignal: Successfully initialized");
    return instance;
  } catch (error) {
    console.error("AppSignal: Failed to initialize", error);
    return createMockInstance();
  }
};

export default initializeAppSignal();
